import { SubOptionsSelectInput } from '@components/inputs/SelectInput/SubOptionsSelectInput';
import {
  useFetchRootCauses,
  useUpdateRootCause,
} from 'api/anomalies/anomalyServices';
import { useFetchCurrentRootCause } from 'api/anomalies/enervisionService';
import { useFeatureFlag } from 'context/FeatureFlagProvider/FeatureFlagProvider';
import { t } from 'i18next';
import { CardContainer, CardTitle } from 'layout/Card';
import { FEATURE_FLAGS_LIST } from 'lib/featureFlagsList';
import { type FC, useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import NewsImg from '@assets/images/news.svg';
import EnervisionComponent from './EnervisionComponent';

interface RootCauseProps {
  changeId: string | undefined;
  isLoading?: boolean;
}

const RootCause: FC<RootCauseProps> = ({ changeId, isLoading }) => {
  const { mutate, isPending: isUpdatingRootCause } =
    useUpdateRootCause(changeId);
  const { data, isLoading: isLoadingRootCauses } = useFetchRootCauses();
  const { data: currentRootCauseData, isFetching: isFetchingCurrentRootCause } =
    useFetchCurrentRootCause(changeId);
  const { hasFeatureFlagPermission } = useFeatureFlag();

  const currentRootCause = currentRootCauseData?.root_cause;

  const [selectedRootCause, setSelectedRootCause] = useState<
    number | undefined
  >(undefined);

  const isAutoAssigned = currentRootCause?.auto_assigned ?? false;

  const submit = (id: string | number | undefined): void => {
    if (id) {
      const body = {
        root_cause_id: Number(id),
      };
      mutate(body);
    } else {
      // Clear the root cause by sending null
      const body = {
        root_cause_id: null,
      };
      mutate(body);
    }
  };

  useEffect(() => {
    if (currentRootCause?.id !== undefined) {
      setSelectedRootCause(currentRootCause.id);
    } else if (currentRootCause === null) {
      // API returned null, meaning no root cause is set
      setSelectedRootCause(undefined);
    }
  }, [currentRootCause]);

  return (
    <CardContainer>
      <CardTitle
        title={t('anomaliesDetailsPage.rootCause.title')}
        subtitle={t('anomaliesDetailsPage.rootCause.subTitle')}
        style={{ fontSize: 14 }}
        isLoading={isLoading}
        skeletonVariant="subtitle"
      />

      <div className="flex flex-col gap-4">
        {hasFeatureFlagPermission(
          FEATURE_FLAGS_LIST.changes.possible_root_causes
        ) ? (
          isLoading ? (
            <Skeleton height={415} />
          ) : (
            <EnervisionComponent
              anomalyId={changeId}
              isAutoAssigned={isAutoAssigned}
            />
          )
        ) : null}
        <div>
          {isLoading ? (
            <Skeleton width={80} />
          ) : (
            <div className="flex items-center gap-1 mb-2">
              {isAutoAssigned && <img src={NewsImg} height={16} width={16} />}
              <p className="font-bold">
                {isAutoAssigned ? 'Root Cause - Auto Assigned' : 'Root cause'}
              </p>
            </div>
          )}
          {isLoading ? (
            <Skeleton borderRadius={'8px'} height={'40px'} />
          ) : (
            <div className="relative">
              <SubOptionsSelectInput
                options={data ?? []}
                placeholder={t('anomaliesDetailsPage.select.selectLabel')}
                value={selectedRootCause}
                onChange={(value) => {
                  if (value === '' || value === undefined) {
                    setSelectedRootCause(undefined);
                    submit(undefined);
                  } else {
                    setSelectedRootCause(value as number);
                    submit(value);
                  }
                }}
                disabled={
                  isLoadingRootCauses ||
                  isFetchingCurrentRootCause ||
                  isUpdatingRootCause ||
                  !hasFeatureFlagPermission(FEATURE_FLAGS_LIST.changes.patch)
                }
                isLoading={
                  isLoadingRootCauses ||
                  isFetchingCurrentRootCause ||
                  isUpdatingRootCause
                }
              />
            </div>
          )}
        </div>
      </div>
    </CardContainer>
  );
};
export default RootCause;
